// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
/** Set when saved rows could not be read: the app still works, in memory. */
export const storeError = ref(null);

const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch((e) => {     // no IndexedDB (SSR, private mode) — stay in memory, and say so
  storeError.value = e?.message || 'storage unavailable';
  return [];
});

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

// Demo times are CLOCK times from the plan's story ("6:10 am"), on today's
// date, so a capture reads like the morning it describes rather than "3 years ago".
const atClock = (h, m, days = 0) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(h, m, 0, 0); return d.toISOString(); };

/** One lap of the Marina Bay street circuit: the car, the time and the result. Shown on: Grid, Drive, Chequered flag, Champagne, Garage. */
export const laps = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededLap = demoData([
  { id: -1, summary: 'Lap of Marina Bay', status: 'best-lap', createdAt: atClock(18, 52), car: 'Lamborghini Huracan', track: 'Marina Bay Street Circuit', controls: 'Steer, brake', speed: '284 km/h', lapTime: '1:18.6', position: 'P1', corner: 'Turn 7', result: 'Winner', bestLap: '1:18.6', reward: 'Champagne', timeMs: 78600, place: 1 },
  { id: -2, summary: 'Lap of Marina Bay', status: 'track', createdAt: atClock(18, 40), car: 'Lamborghini Huracan', track: 'Marina Bay Street Circuit', controls: 'Steer, brake', speed: '276 km/h', lapTime: '1:22.3', position: 'P3', corner: 'Turn 7', result: 'Finished P3', bestLap: '1:22.3', reward: '', timeMs: 82300, place: 3 },
]);
watch(seededLap, (rows) => {
  if (rows.length && !laps.value.length) laps.value = [...rows];
}, { immediate: true });

export const lapsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'lap');
  if (mine.length) laps.value = mine;
  return laps.value;
});

export function addLap(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'lap',
    summary: input.summary ?? '',
    status: input.status ?? '',
    createdAt: input.createdAt ?? new Date().toISOString(),
    car: input.car ?? '',
    track: input.track ?? '',
    controls: input.controls ?? '',
    speed: input.speed ?? '',
    lapTime: input.lapTime ?? '',
    position: input.position ?? '',
    corner: input.corner ?? '',
    result: input.result ?? '',
    bestLap: input.bestLap ?? '',
    reward: input.reward ?? '',
  };
  laps.value = [row, ...laps.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveLap(row) {
  laps.value = laps.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeLap(id) {
  laps.value = laps.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findLap = (id) =>
  laps.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentLap = computed(() => laps.value.slice(0, 3));
export const lapsTitle = (row) => row?.summary;

/** A car in the garage: ready to drive, or coming next. Shown on: Garage. */
export const sampleCars = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededSampleCar = demoData([
  { id: -11, name: 'Lamborghini Huracan', summary: 'Red, number 1. Yours for every lap of Marina Bay.', status: 'ready' },
  { id: -12, name: 'Blue V8 supercar', summary: 'Choose your car, in the next version.', status: 'coming-next' },
  { id: -13, name: 'Yellow V8 supercar', summary: 'Choose your car, in the next version.', status: 'coming-next' },
  { id: -14, name: 'Pit lane and tyres', summary: 'Pull into the pits and change tyres mid-race.', status: 'coming-next' },
]);
watch(seededSampleCar, (rows) => {
  if (rows.length && !sampleCars.value.length) sampleCars.value = [...rows];
}, { immediate: true });

export const sampleCarsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'sample-car');
  if (mine.length) sampleCars.value = mine;
  return sampleCars.value;
});

export function addSampleCar(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'sample-car',
    name: input.name ?? '',
    summary: input.summary ?? '',
    status: input.status ?? '',
  };
  sampleCars.value = [row, ...sampleCars.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveSampleCar(row) {
  sampleCars.value = sampleCars.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeSampleCar(id) {
  sampleCars.value = sampleCars.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findSampleCar = (id) =>
  sampleCars.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentSampleCar = computed(() => sampleCars.value.slice(0, 3));
export const sampleCarsTitle = (row) => row?.name;
